import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { forwardRef, useContext } from 'react';
import styled from 'styled-components';
import { buttonStyles } from './baseButtonStyles.js';
export { buttonDisabledStyles, buttonFocusStyles, buttonStyles } from './baseButtonStyles.js';
import { NicolletContext } from '../NicolletContext/index.js';
import { AppContext } from '../AppContext/index.js';

/**
 * Please note that while the `isFullWidth` prop is being passed in, it is not being immediately used.
 * Instead, `isFullWidth` is being passively consumed by the BaseButton Styled Component
 */

const BaseButtonInternal = forwardRef((props, ref) => {
  const {
    isFullWidth,
    size = 'md',
    renderLeading,
    children,
    href,
    rel,
    type = 'button',
    // Extract Next.js Link props so we don't forward to anchors or buttons, only to `RouterLink`.
    prefetch,
    replace,
    scroll,
    shallow,
    ...restProps
  } = props;
  const {
    nicollet = {}
  } = useContext(NicolletContext);
  const appContext = useContext(AppContext.Context);
  const {
    isRouterLinkNextLink
  } = appContext; // In Web Platform sites, RouterLink is passed as a Next.js `next/link`.
  // TODO: Once all Web Platform foundation is providing `isRouterLinkNextLink: true` and all sites have upgraded,
  // we can consume from `appContext` either way. Until then it's not safe, so use legacy context.

  const {
    RouterLink = 'a'
  } = isRouterLinkNextLink ? appContext : nicollet;

  const inner = jsxs(Fragment, {
    children: [renderLeading, children]
  }, void 0);

  if (href) {
    // This logic to clean up `href` and wrap in `RouterLink` is similar to the `Link` component.
    // TODO: Perhaps just use `Link` directly here, but unsure of differences in style or behavior.
    const hrefTrimmed = href ? href === null || href === void 0 ? void 0 : href.trim() : undefined;
    return isRouterLinkNextLink ? // In Web Platform sites, `RouterLink` is provided as a Next.js `next/link`,
    // so we need to wrap the children in an anchor tag internally.
    jsx(RouterLink, Object.assign({
      href: hrefTrimmed,
      // Add the Next.js Link props back for forwarding to `RouterLink`.
      prefetch: prefetch,
      replace: replace,
      scroll: scroll,
      shallow: shallow
    }, {
      children: jsx("a", Object.assign({
        href: hrefTrimmed,
        // @ts-expect-error Not sure why TS won't let us pass `ref` here.
        ref: ref,
        rel: rel,
        role: "button",
        size: size
      }, restProps, {
        children: inner
      }), void 0)
    }), void 0) : jsx(RouterLink, Object.assign({
      href: hrefTrimmed,
      ref: ref,
      rel: rel,
      role: "button"
    }, restProps, {
      children: inner
    }), void 0);
  }

  return (// eslint-disable-next-line react/button-has-type
    jsx("button", Object.assign({
      ref: ref,
      type: type
    }, restProps, {
      children: inner
    }), void 0)
  );
});
const BaseButton = styled(BaseButtonInternal).withConfig({
  /**
   * 'isActive', 'isOutOfStock', and 'rotation' are invalid HTML button attributes, so we need to filter them out.
   */
  shouldForwardProp: prop => !['isActive', 'isOutOfStock', 'rotation'].includes(prop)
})`
  ${buttonStyles}
`;
BaseButtonInternal.displayName = 'BaseButtonInternal';
BaseButton.displayName = 'BaseButton';
/* defaultProps is still needed for components wrapped in forwardRef() until https://github.com/styleguidist/react-docgen-typescript/issues/254 is fixed upstream. */

BaseButton.defaultProps = {
  size: 'md'
};

export { BaseButton };
